import { useMatomo } from '@datapunt/matomo-tracker-react';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import React, { useEffect, useRef } from 'react';

import Header from '@components/common/Header/Header';
import CopyLinkIconBtn from '@components/common/Header/components/BtnHeaderCopyLink/BtnHeaderCopyLink';
import DownloadIconBtn from '@components/common/Header/components/BtnHeaderDownload/BtnHeaderDownload';
import PrintIconBtn from '@components/common/Header/components/BtnHeaderPrint/BtnHeaderPrint';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { ILocation } from '@model/structureData';

import { useReportActionsMutation } from '@services/report/useReportActionsMutation';
import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import TableHardware from './components/TableHardware/TableHardware';
import style from './style.module.scss';

const HardwareOverview = () => {
  const { t } = useTranslation();
  const { trackPageView } = useMatomo();
  const printRef = useRef<HTMLInputElement>(null);

  const { data: profileData } = useUserProfileQuery();
  const { data: assetTreeData } = useAssetTreeQuery();

  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const { mutate: reportActionsMutate } = useReportActionsMutation();

  useEffect(() => {
    trackPageView({});
  }, []);

  const downloadHardwareOverviewReport = () => {
    const allFacilitiesIds = assetTreeData?.facilities?.map((facility: ILocation) => facility.id);

    showToast({ variant: 'success', message: t('The download will begin shortly.') });
    reportActionsMutate(
      {
        action: 'hardwareOverview',
        locations: allFacilitiesIds,
      },
      {
        onSuccess: (result: any) => {
          saveAs(
            new Blob([result?.data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
            }),
            `${profileData?.customer?.name || ''} ${t('Hardware Overview Report')} ${format(
              new Date(),
              currentDateFormat,
            )}.xlsx`,
          );
        },
      },
    );
  };

  return (
    <div className={style.hardwareOverview}>
      <Header
        title={t('Hardware Overview')}
        showLogo={true}
        headerRightComponent={
          <>
            <DownloadIconBtn
              tooltipInfo={t('Download Hardware Overview Report')}
              downloadFn={downloadHardwareOverviewReport}
            />
            <CopyLinkIconBtn />
            <PrintIconBtn printRef={printRef} />
          </>
        }
      />
      <div className={style.hardwareOverviewBody} ref={printRef}>
        <TableHardware />
      </div>
    </div>
  );
};

export default HardwareOverview;
